import { useNavigate, useOutletContext } from '@remix-run/react';
import { BadgePercent, Loader2Icon, Plus } from 'lucide-react';
import { useRef, useState } from 'react';
import { Price } from '~/components/products/Price';
import { DetailedProductFragment } from '~/generated/graphql';
import { OutletContext } from '~/types';
import { calculateEffectivePrice } from '~/utils/pricing';

interface SuperSaverSectionProps {
  products: DetailedProductFragment[];
}

export function SuperSaverSection({ products }: SuperSaverSectionProps) {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);
  const [moved, setMoved] = useState(false);

  const handleMouseDown = (e: React.MouseEvent) => {
    if (!scrollContainerRef.current) return;
    setIsDragging(true);
    setMoved(false);
    setStartX(e.pageX - scrollContainerRef.current.offsetLeft);
    setScrollLeft(scrollContainerRef.current.scrollLeft);
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDragging || !scrollContainerRef.current) return;
    e.preventDefault();
    const x = e.pageX - scrollContainerRef.current.offsetLeft;
    const walk = (x - startX) * 2;
    if (Math.abs(x - startX) > 5) {
      setMoved(true);
    }
    scrollContainerRef.current.scrollLeft = scrollLeft - walk;
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleMouseLeave = () => {
    setIsDragging(false);
  };

  return (
    <section className="w-full py-3 sm:py-4 lg:py-6">
      <div
        className="relative rounded-[20px] overflow-hidden flex flex-col lg:flex-row h-auto lg:h-[426px] p-4 lg:p-6 gap-4 lg:gap-6"
        style={{
          background: 'linear-gradient(90deg, #E80245 0%, #FA479E 100%)',
        }}
      >
        {/* Background Decor Component - The lightning bolt shape */}
        <div className="absolute top-0 left-0 w-full h-full pointer-events-none z-0">
          {/* Secondary subtle gradient for richness */}
          <div
            className="absolute inset-0 opacity-30"
            style={{
              background:
                'linear-gradient(135deg, #FF857B 0%, #EA4335 50%, #E8178A 100%)',
              mixBlendMode: 'overlay',
            }}
          ></div>
          <img
            src="/super-saver-decor.png"
            alt="Decor"
            className="absolute left-0 top-0 h-full w-auto object-contain opacity-50 -translate-x-12"
          />
        </div>
        {/* Banner Side */}
        <div className="flex flex-col justify-center text-white relative z-10 w-full lg:w-[254px] shrink-0 gap-[5px]">
          <div className="flex items-start gap-2 mb-2 lg:mb-4">
            <h2
              style={{
                fontFamily: 'Poppins, sans-serif',
                fontWeight: 600,
              }}
              className="text-xl sm:text-2xl lg:text-[28px] leading-tight lg:leading-[36px] tracking-tight text-white"
            >
              🔥 Super Saver: 50% off
            </h2>
          </div>
          <p
            style={{
              fontFamily: 'Poppins, sans-serif',
              fontWeight: 500,
            }}
            className="text-sm lg:text-[16px] leading-normal lg:leading-[24px] tracking-tight text-white/80"
          >
            Don't miss out on the hottest gifts of the holidays.
          </p>
        </div>

        {/* Products Side - Fixed with min-w-0 for overflow-x-auto */}
        <div className="flex-1 relative z-10 flex items-center min-w-0">
          <div
            ref={scrollContainerRef}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseLeave}
            style={{
              gap: '16px',
              scrollbarWidth: 'none',
              msOverflowStyle: 'none',
              userSelect: isDragging ? 'none' : 'auto',
            }}
            className="flex overflow-x-auto [&::-webkit-scrollbar]:hidden pb-2 lg:pb-4 w-full cursor-grab active:cursor-grabbing"
          >
            {products.map((product) => (
              <div key={product.id} className="shrink-0">
                <SuperSaverProductCard product={product} moved={moved} />
              </div>
            ))}
            {/* Spacing at the end for mobile */}
            <div className="shrink-0 w-4 lg:w-12 h-1" />
          </div>
        </div>
      </div>
    </section>
  );
}

function SuperSaverProductCard({
  product,
  moved,
}: {
  product: DetailedProductFragment;
  moved: boolean;
}) {
  const navigate = useNavigate();
  const { activeOrderFetcher: fetcher, merchantContextRequired } =
    useOutletContext<OutletContext>();
  const [quantity, setQuantity] = useState(1);

  const variant = product.variants[0];
  const isBeingAddedToCart =
    fetcher.state === 'submitting' &&
    fetcher.formData?.get('variantId') === variant?.id;

  const basePrice =
    typeof variant?.priceWithTax === 'number'
      ? variant.priceWithTax
      : (variant?.priceWithTax as any)?.value || 0;
  const currencyCode = variant?.currencyCode;

  const priceTiers = variant?.customFields?.priceTiers || [];

  const { effectiveUnitPrice } = calculateEffectivePrice(
    basePrice || 0,
    priceTiers,
    quantity,
  );

  const handleAddToCart = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (!fetcher || !variant?.id) return;

    fetcher.submit(
      {
        action: 'addItemToOrder',
        variantId: variant.id,
        quantity: quantity.toString(),
      },
      {
        method: 'post',
        action: '/api/active-order',
      },
    );
  };

  const handleProductClick = () => {
    if (!moved) {
      navigate(`/products/${product.slug}`);
    }
  };

  return (
    <div className="w-[185px] sm:w-[210px] md:w-[230px] bg-white rounded-[24px] overflow-hidden flex flex-col h-full shadow-sm hover:shadow-md transition-shadow duration-300">
      {/* Image Container - Subtle light background */}
      <div className="relative p-2 pb-0">
        <div
          className="aspect-square rounded-2xl overflow-hidden cursor-pointer flex items-center justify-center bg-gray-50/50"
          onClick={handleProductClick}
        >
          {product.featuredAsset?.preview ? (
            <img
              src={`${product.featuredAsset.preview}?w=300&h=300&format=webp`}
              alt={product.name}
              className="w-full h-full object-contain hover:scale-105 transition-transform duration-300"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center opacity-20">
              <img src="/brand_logo.png" alt="Logo" className="w-1/2 h-auto" />
            </div>
          )}
        </div>

        {/* Floating Plus Button */}
        <button
          onClick={handleAddToCart}
          disabled={isBeingAddedToCart || merchantContextRequired}
          className="absolute bottom-2 right-2 w-9 h-9 rounded-full bg-white border border-gray-100 shadow-sm flex items-center justify-center text-gray-600 hover:text-[#E80245] hover:border-[#E80245]/30 active:scale-95 transition-all duration-200 z-10"
        >
          {isBeingAddedToCart ? (
            <Loader2Icon size={18} className="animate-spin text-[#E80245]" />
          ) : (
            <Plus size={20} strokeWidth={2} />
          )}
        </button>
      </div>

      {/* Content */}
      <div className="p-4 pt-3 flex flex-col flex-1">
        {/* Price */}
        <div className="flex items-baseline gap-2 mb-1">
          <span className="text-gray-900 font-bold text-lg tracking-tight">
            <Price
              priceWithTax={effectiveUnitPrice}
              currencyCode={currencyCode}
            />
          </span>
        </div>

        {/* Product Name */}
        <h3
          className="text-sm font-normal text-gray-800 line-clamp-2 leading-snug cursor-pointer hover:text-[#E80245] transition-colors"
          onClick={handleProductClick}
        >
          {product.name}
        </h3>

        {/* Short Description */}
        <p className="text-xs text-gray-500 mt-1 mb-3">
          {product.customFields?.shortDescription || '400ml • 1 pcs'}
        </p>

        {/* Bulk Pricing */}
        <div className="flex flex-col gap-1.5 mt-auto">
          {priceTiers.length > 0 ? (
            priceTiers.slice(0, 2).map((tier, idx) => (
              <div
                key={idx}
                className="flex items-center gap-1.5 text-[#E80245] text-[11px] font-medium leading-none"
              >
                <BadgePercent size={14} className="flex-shrink-0" />
                <span>
                  <Price
                    priceWithTax={tier.price ?? 0}
                    currencyCode={currencyCode}
                    variant="primary"
                  />
                  /pack for {tier.minQty}+
                </span>
              </div>
            ))
          ) : (
            <div className="h-4 invisible" aria-hidden="true" />
          )}
        </div>
      </div>
    </div>
  );
}
